import React from 'react';
import axios from 'axios';
import { Clock, ExternalLink, X } from 'lucide-react';
import { getBackendUrl } from '../../../services/origin';
import { TIER_COLORS } from '../constants';
import { QrToggle } from '../QrToggle';
import { CouponQRScanner } from '../CouponQRScanner';
import { SocialProofSection } from '../SocialProofSection';
import CookieFooterLink from '../../../components/CookieFooterLink';
import { formatRedemptionConditionsShort, isRedeemableNow } from '../../../utils/redemptionConditions';

const API = getBackendUrl();

/* ── HOME TAB ──────────────────────────────────────────────── */
export function HomeTab({ data, brand, tc, ts, tierColor, tierEmoji, tierNames, tierColorsCustom, tierPct, pushEnabled, onPushToggle, token, pin, setData }) {
  return (
    <div className="animate-fade-in-up">
      {/* Promo banners — native-app card style: full-width, rounded-3xl,
          generous padding, big gap from next section */}
      {(data.dashboard_messages?.banners || []).map((b, i) => (
        <div
          key={i}
          className="rounded-3xl overflow-hidden border mb-5 shadow-sm"
          style={{ borderColor: `${tc}10`, backgroundColor: `${brand.primary_color}0a` }}
          data-testid={i === 0 ? 'promo-banner' : `promo-banner-${i}`}
        >
          {b.image_url && <img src={b.image_url} alt="" className="w-full h-40 object-cover" />}
          {(b.title || b.description) && (
            <div className="p-5">
              {b.title && <p className="font-bold text-base" style={{ color: brand.primary_color }}>{b.title}</p>}
              {b.description && <p className="text-sm mt-1.5" style={{ color: ts }}>{b.description}</p>}
            </div>
          )}
        </div>
      ))}

      {/* HERO POINTS CARD — dominant, full-width, native-app feel.
          Number is the visual anchor (text-7xl). Tier badge + progress
          live below as supporting info, never competing with the number. */}
      <div
        className="rounded-3xl p-7 border shadow-md mb-5 relative overflow-hidden"
        style={{ backgroundColor: `${tc}08`, borderColor: `${tc}10` }}
        data-testid="points-card"
      >
        {/* Soft brand-color glow in the corner — adds depth without
            stealing focus from the number */}
        <div
          className="absolute -right-12 -top-12 w-40 h-40 rounded-full opacity-20 blur-2xl pointer-events-none"
          style={{ backgroundColor: brand.primary_color }}
        />

        <p className="text-sm font-semibold uppercase tracking-wider" style={{ color: `${ts}cc` }}>
          I tuoi punti
        </p>
        <div className="flex items-end justify-between gap-3 mt-2">
          <p
            className="font-black tabular-nums leading-none tracking-tight"
            style={{ color: tc, fontSize: 'clamp(56px, 18vw, 84px)' }}
          >
            {data.points}
          </p>
          <div className="text-right pb-2">
            <span
              className="inline-flex items-center gap-1 px-4 py-2 rounded-full text-base font-bold capitalize"
              style={{ backgroundColor: `${tierColor}25`, color: tierColor }}
            >
              {tierEmoji} {tierNames[data.tier] || data.tier}
            </span>
            <p className="text-xs mt-2" style={{ color: `${ts}aa` }}>{data.total_visits} visite</p>
          </div>
        </div>

        {data.next_tier && (
          <div data-testid="tier-progress" className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium" style={{ color: ts }}>
                Prossimo:{' '}
                <span className="capitalize font-bold" style={{ color: tierColorsCustom[data.next_tier] || TIER_COLORS[data.next_tier] }}>
                  {tierNames[data.next_tier] || data.next_tier}
                </span>
              </span>
              <span className="text-sm tabular-nums font-semibold" style={{ color: tc }}>
                {data.lifetime_points}/{data.next_tier_points}
              </span>
            </div>
            <div className="h-3 rounded-full overflow-hidden" style={{ backgroundColor: `${tc}10` }}>
              <div
                className="h-full rounded-full animate-progress"
                style={{ width: `${tierPct}%`, backgroundColor: TIER_COLORS[data.next_tier] }}
              />
            </div>
            <p className="text-xs mt-2 tabular-nums" style={{ color: `${ts}aa` }}>
              Mancano {data.next_tier_points - data.lifetime_points} pt
            </p>
          </div>
        )}
        {!data.next_tier && (
          <div className="flex items-center gap-2 mt-5">
            <span className="text-xl">👑</span>
            <p className="text-base font-bold" style={{ color: brand.secondary_color }}>
              Livello massimo raggiunto!
            </p>
          </div>
        )}
      </div>

      {/* Next reward progress */}
      {(() => {
        const nextReward = [...(data.rewards || [])].sort((a, b) => a.points_cost - b.points_cost).find(r => r.points_cost > data.points);
        if (!nextReward) {
          if ((data.rewards || []).length > 0 && data.points > 0) {
            return (
              <div className="rounded-2xl p-4 border mb-4" style={{ backgroundColor: `${brand.secondary_color}08`, borderColor: `${brand.secondary_color}20` }} data-testid="all-rewards-unlocked">
                <p className="text-xs font-semibold" style={{ color: brand.secondary_color }}>Puoi riscattare tutti i premi disponibili!</p>
              </div>
            );
          }
          return null;
        }
        const pct = Math.min(100, Math.round((data.points / nextReward.points_cost) * 100));
        const remaining = nextReward.points_cost - data.points;
        return (
          <div
            className="rounded-3xl p-6 border mb-5 shadow-sm"
            style={{ backgroundColor: `${tc}06`, borderColor: `${brand.primary_color}20` }}
            data-testid="next-reward-progress"
          >
            <div className="flex items-center justify-between mb-4 gap-3">
              <div className="flex items-center gap-4 min-w-0">
                <span className="text-4xl flex-shrink-0">{nextReward.emoji || '🎁'}</span>
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: `${ts}cc` }}>
                    Prossimo premio
                  </p>
                  <p className="text-base font-bold mt-0.5 truncate" style={{ color: tc }}>
                    {nextReward.name}
                  </p>
                </div>
              </div>
              <span
                className="text-base font-black tabular-nums flex-shrink-0 px-3 py-1.5 rounded-full"
                style={{ backgroundColor: `${brand.primary_color}18`, color: brand.primary_color }}
              >
                {nextReward.points_cost} pt
              </span>
            </div>
            <div className="h-3 rounded-full overflow-hidden mb-2.5" style={{ backgroundColor: `${tc}10` }}>
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{ width: `${pct}%`, backgroundColor: brand.primary_color }}
              />
            </div>
            <p className="text-sm tabular-nums font-medium" style={{ color: `${ts}cc` }}>
              {remaining > 0 ? `Ancora ${remaining} punti — ci sei quasi!` : 'Riscattabile ora!'}
            </p>
          </div>
        );
      })()}

      {/* QR Code — collapsible, kept as-is per user request */}
      {data.phone && (
        <section className="mb-5" data-testid="qr-section">
          <QrToggle phone={data.phone} brand={brand} tc={tc} ts={ts} />
        </section>
      )}

      {/* QR Scanner — read coupon QR codes without leaving the dashboard */}
      <section className="mb-5" data-testid="qr-scanner-section">
        <CouponQRScanner token={token} pin={pin} brand={brand} tc={tc} ts={ts} setData={setData} />
      </section>

      {/* Credited Rewards (gifts) */}
      {(data.credited_rewards || []).length > 0 && (
        <section className="mb-5" data-testid="credited-rewards-section">
          <h2 className="text-xl font-bold mb-4" style={{ color: tc }}>I tuoi regali</h2>
          <div className="space-y-4">
            {data.credited_rewards.map((cr, i) => {
              const daysLeft = cr.expires_at ? Math.max(0, Math.ceil((new Date(cr.expires_at) - new Date()) / (1000 * 60 * 60 * 24))) : null;
              const sourceLabels = { birthday: 'Compleanno', second_visit_month: '2a visita', tenth_visit_year: '10a visita', challenge: 'Challenge', milestone_second_visit: '2a visita', milestone_tenth_visit: '10a visita' };
              const conditionsLabel = formatRedemptionConditionsShort(cr.redemption_conditions);
              const redeemableNow = isRedeemableNow(cr.redemption_conditions, data);
              return (
                <div key={i} data-testid={`credited-reward-${i}`} className="rounded-3xl p-5 border shadow-sm" style={{ backgroundColor: `${tc}06`, borderColor: `${brand.secondary_color}30` }}>
                  <div className="flex items-center gap-4">
                    <span className="text-4xl flex-shrink-0">{cr.emoji || '🎁'}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-base" style={{ color: tc }}>{cr.description}</p>
                      <p className="text-sm mt-1" style={{ color: ts }}>{sourceLabels[cr.source] || cr.source_detail}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <span className="text-xs font-bold px-3 py-1.5 rounded-full" style={{ backgroundColor: `${brand.secondary_color}20`, color: brand.secondary_color }}>Da ritirare</span>
                      {daysLeft !== null && (
                        <p className="text-xs mt-2 font-medium" style={{ color: daysLeft <= 3 ? '#FFAA00' : ts }}>
                          {daysLeft <= 0 ? 'Scade oggi!' : `Scade tra ${daysLeft}g`}
                        </p>
                      )}
                    </div>
                  </div>
                  {conditionsLabel && (
                    <div
                      className="mt-3 text-xs font-medium px-3 py-2 rounded-xl flex items-center gap-2"
                      style={{
                        backgroundColor: redeemableNow ? `${brand.secondary_color}12` : '#FCA5A520',
                        color: redeemableNow ? brand.secondary_color : '#DC2626',
                      }}
                      data-testid={`credited-reward-conditions-${i}`}
                    >
                      <Clock size={12} className="flex-shrink-0" />
                      <span className="truncate">{conditionsLabel}</span>
                      {!redeemableNow && <span className="ml-auto whitespace-nowrap font-bold">Non oggi</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Expiry warning — visible only within the final 30-day window
          (or earlier if already expiring soon). `days_until_expiry` is
          computed server-side: relative mode uses the customer's oldest
          earning transaction, fixed/recurring modes use the resolved
          calendar date. Hidden otherwise to avoid alarming customers
          months in advance. */}
      {data.expiry_info && typeof data.expiry_info.days_until_expiry === 'number'
        && data.expiry_info.days_until_expiry >= 0
        && data.expiry_info.days_until_expiry <= 30 && (
        <div className="bg-[#FFAA00]/10 border border-[#FFAA00]/20 rounded-2xl p-4 mb-4 flex items-start gap-3" data-testid="expiry-warning">
          <Clock size={20} className="text-[#FFAA00] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-[#FFAA00] text-sm font-semibold">
              {data.expiry_info.days_until_expiry === 0
                ? 'I tuoi punti scadono oggi!'
                : `I tuoi punti scadono tra ${data.expiry_info.days_until_expiry} giorn${data.expiry_info.days_until_expiry === 1 ? 'o' : 'i'}`}
            </p>
            <p className="text-[#FFAA00]/80 text-xs mt-0.5">
              {data.expiry_info.date
                ? `Scadenza: ${data.expiry_info.date}. `
                : ''}
              Usa i tuoi punti prima che scadano!
            </p>
          </div>
        </div>
      )}

      {/* Review Bonus Banner (one-time, after click) */}
      {data.review_click_status?.unseen_bonus && data.review_click_status?.points > 0 && (
        <div className="mb-4 rounded-2xl p-4 border relative" data-testid="review-bonus-banner"
          style={{ backgroundColor: `${brand.secondary_color || '#3ECF8E'}15`, borderColor: `${brand.secondary_color || '#3ECF8E'}30` }}>
          <button onClick={() => {
            axios.post(`${API}/api/my/${token}/review-click-seen`, { pin }).catch(() => {});
            setData(d => ({ ...d, review_click_status: { ...d.review_click_status, unseen_bonus: false } }));
          }} className="absolute top-3 right-3 opacity-60 hover:opacity-100" data-testid="review-bonus-dismiss">
            <X size={14} style={{ color: tc }} />
          </button>
          <p className="font-bold text-sm" style={{ color: brand.secondary_color || '#3ECF8E' }}>
            Grazie per aver lasciato una recensione!
          </p>
          <p className="text-xs mt-1" style={{ color: ts }}>
            Hai ricevuto <strong style={{ color: tc }}>{data.review_click_status.points} punti bonus</strong> come ringraziamento.
          </p>
        </div>
      )}

      {/* Review Links */}
      {(data.review_links || []).length > 0 && (
        <section className="mb-4" data-testid="review-links-section">
          <h2 className="text-base font-bold mb-3" style={{ color: tc }}>Lascia una recensione</h2>
          {!data.review_click_status?.awarded && data.review_click_bonus_points > 0 && (
            <p className="text-xs mb-3" style={{ color: brand.secondary_color || '#3ECF8E' }} data-testid="review-incentive-text">
              Lascia una recensione e ricevi <strong>{data.review_click_bonus_points} punti bonus</strong>!
            </p>
          )}
          {data.social_proof_config?.enabled && data.social_proof_config?.review_screenshot_bonus > 0 && (
            <p className="text-xs mb-3" style={{ color: '#F59E0B' }}>
              Carica lo screenshot della recensione per ricevere <strong>{data.social_proof_config.review_screenshot_bonus} punti extra</strong>!
            </p>
          )}
          <div className="space-y-2">
            {data.review_links.map((r, i) => {
              const platforms = {
                google: { color: '#4285F4', icon: 'G', label: 'Google' },
                tripadvisor: { color: '#34E0A1', icon: 'TA', label: 'TripAdvisor' },
                facebook: { color: '#1877F2', icon: 'f', label: 'Facebook' },
              };
              const p = platforms[r.platform] || { color: brand.primary_color, icon: r.name?.charAt(0) || '?', label: r.name };
              return (
                <a key={i} href={r.url} target="_blank" rel="noopener noreferrer" data-testid={`review-link-${r.platform}`}
                  onClick={() => {
                    try { axios.post(`${API}/api/my/${token}/review-click`, { pin, platform: r.platform }).catch(() => {}); } catch {}
                  }}
                  className="flex items-center gap-3 rounded-2xl p-4 border transition-colors active:opacity-80" style={{ backgroundColor: `${tc}06`, borderColor: `${tc}08` }}>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: p.color }}>
                    {p.icon}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-sm" style={{ color: tc }}>{r.name || p.label}</p>
                    <p className="text-xs" style={{ color: ts }}>Lascia una recensione su {p.label}</p>
                  </div>
                  <ExternalLink size={16} style={{ color: ts }} />
                </a>
              );
            })}
          </div>
        </section>
      )}

      {/* Social Proof — Upload Screenshots */}
      {data.social_proof_config?.enabled && (
        <SocialProofSection data={data} token={token} pin={pin} brand={brand} tc={tc} ts={ts} setData={setData} />
      )}

      {/* Cookie consent management — discreet icon footer, GDPR art. 7.3 */}
      <div className="mt-8 mb-4 flex justify-center" style={{ color: ts }}>
        <CookieFooterLink />
      </div>
    </div>
  );
}
